import React, { useMemo, useState } from "react";
import { Slider } from "./ui/slider";
import LeadForm from "./LeadForm";

const fmt = (n) => `${Math.round(n).toLocaleString("it-IT")} €`;
const TAN = 7.9;
const TAEG = 8.9;
const DURATIONS = [36, 48, 60, 72, 84];

export default function Finance({ price = 0 }) {
  const [anticipoPct, setAnticipoPct] = useState(20);
  const [durata, setDurata] = useState(48);
  const [leadOpen, setLeadOpen] = useState(false);
  const sliderValue = useMemo(() => [anticipoPct], [anticipoPct]);

  const { anticipo, finanziato, rata } = useMemo(() => {
    const anticipo = (price * anticipoPct) / 100;
    const finanziato = Math.max(price - anticipo, 0);
    const r = TAN / 100 / 12;
    const rata = finanziato > 0 ? (finanziato * r) / (1 - Math.pow(1 + r, -durata)) : 0;
    return { anticipo, finanziato, rata };
  }, [price, anticipoPct, durata]);

  return (
    <section id="finanziamento" className="cm-reveal border-t border-[var(--cm-line)] py-16">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <p className="label-mono mb-5">Finanziamento</p>
          <h2 className="h-display text-[clamp(1.9rem,3.6vw,2.9rem)]">
            Quanto vuoi pagare al mese?
          </h2>
          <p className="mt-5 max-w-[440px] text-[0.98rem] leading-relaxed text-[#5b5b54]">
            Se finanzi l'acquisto con Carmaps ricevi l'esito rapidamente. Ti
            spieghiamo in sede le opzioni di garanzia e assistenza disponibili.
          </p>

          <div className="mt-10">
            <p className="label-mono mb-4">
              Anticipo — {anticipoPct}% ({fmt(anticipo)})
            </p>
            <Slider
              value={sliderValue}
              min={10}
              max={50}
              step={5}
              onValueChange={(v) => setAnticipoPct(v[0])}
              className="[&_[role=slider]]:bg-[var(--cm-green)] [&_.bg-primary]:bg-[var(--cm-green)]"
            />
          </div>

          <div className="mt-9">
            <p className="label-mono mb-4">Durata</p>
            <div className="flex flex-wrap gap-3">
              {DURATIONS.map((m) => (
                <button
                  key={m}
                  onClick={() => setDurata(m)}
                  className={`rounded-lg px-6 py-3 text-[0.9rem] font-medium transition-colors ${
                    durata === m
                      ? "bg-[var(--cm-ink)] text-white"
                      : "border border-[var(--cm-line)] hover:border-[var(--cm-ink)]"
                  }`}
                >
                  {m} mesi
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-[18px] border border-[var(--cm-line)] bg-white p-8">
          <p className="label-mono mb-3">Rata stimata</p>
          <p className="text-[3rem] font-semibold leading-none">
            {fmt(rata)}
            <span className="text-[1rem] font-normal text-[#8a8a82]">/mese</span>
          </p>
          <div className="mt-7 space-y-3 text-[0.95rem]">
            {[
              ["Prezzo vettura", fmt(price)],
              ["Importo finanziato", fmt(finanziato)],
              ["TAN (esempio)", `${TAN}%`],
              ["TAEG (esempio)", `${TAEG}%`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-[var(--cm-line)] pb-3">
                <span className="text-[#6f6f68]">{k}</span>
                <span className="font-medium">{v}</span>
              </div>
            ))}
          </div>
          <p className="mt-5 text-[0.75rem] leading-relaxed text-[#9a9a92]">
            * Simulazione a scopo illustrativo con condizioni di esempio. Non
            costituisce offerta di credito: le condizioni reali vengono definite
            con il preventivo.
          </p>
          <button
            type="button"
            data-testid="finance-cta"
            onClick={() => setLeadOpen(true)}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--cm-ink)] px-6 py-3.5 text-[0.92rem] font-medium text-white transition-transform hover:scale-[1.02] [&_svg]:text-white"
          >
            Richiedi finanziamento
          </button>
        </div>
      </div>
      <LeadForm open={leadOpen} onClose={() => setLeadOpen(false)} mode="info" />
    </section>
  );
}
